import html2canvas from "html2canvas";
import { createMonotonicProgress, createScopedProgress } from "./exportProgressUtils";
import { pickVideoMimeType } from "./videoMimeUtils";

export type { ExportProgressCallback } from "./videoTranscodeUtils";

export const STORY_WIDTH = 540;
export const STORY_HEIGHT = 960;
export const STORY_ASPECT_RATIO = STORY_WIDTH / STORY_HEIGHT;
export const STORY_SIDE_PADDING = 40;
export const STORY_TOP_PADDING = 80;
export const STORY_BOTTOM_PADDING = 80;
export const STORY_CONTENT_WIDTH = STORY_WIDTH - STORY_SIDE_PADDING * 2;

export const MOBILE_VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  facingMode: "user",
  width: { ideal: 1080 },
  height: { ideal: 1920 },
};

export const downloadBlob = (blob: Blob, filename: string) => {
  if (blob.size === 0) {
    throw new Error("Empty download blob");
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();

  window.setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 200);
};

const decodeAudioBlob = async (
  audioBlob: Blob,
): Promise<{ buffer: AudioBuffer; audioCtx: AudioContext }> => {
  const arrayBuffer = await audioBlob.arrayBuffer();
  const audioCtx = new AudioContext();
  await audioCtx.resume();
  const buffer = await audioCtx.decodeAudioData(arrayBuffer);
  return { buffer, audioCtx };
};

const waveformFromBuffer = (buffer: AudioBuffer, barCount = 40) => {
  const channelData = buffer.getChannelData(0);
  const blockSize = Math.max(1, Math.floor(channelData.length / barCount));
  const samples: number[] = [];

  for (let i = 0; i < barCount; i++) {
    let sum = 0;
    for (let j = 0; j < blockSize; j++) {
      sum += Math.abs(channelData[i * blockSize + j] ?? 0);
    }
    samples.push(sum / blockSize);
  }

  const max = Math.max(...samples, 0.001);
  return {
    samples: samples.map((s) => s / max),
    duration: buffer.duration,
  };
};

export const decodeAudioForWaveform = async (
  audioBlob: Blob,
  barCount = 40,
): Promise<{ samples: number[]; duration: number }> => {
  try {
    const { buffer, audioCtx } = await decodeAudioBlob(audioBlob);
    const result = waveformFromBuffer(buffer, barCount);
    await audioCtx.close();
    return result;
  } catch {
    const duration = await estimateAudioDuration(audioBlob);
    return {
      samples: Array.from({ length: barCount }, () => 0.35),
      duration,
    };
  }
};

const estimateAudioDuration = async (audioBlob: Blob): Promise<number> => {
  const audio = document.createElement("audio");
  const url = URL.createObjectURL(audioBlob);
  audio.src = url;

  try {
    await new Promise<void>((resolve, reject) => {
      const timeout = window.setTimeout(
        () => reject(new Error("Audio metadata timeout")),
        5000,
      );
      audio.onloadedmetadata = () => {
        window.clearTimeout(timeout);
        resolve();
      };
      audio.onerror = () => {
        window.clearTimeout(timeout);
        reject(new Error("Failed to load audio"));
      };
    });
    return Number.isFinite(audio.duration) ? audio.duration : 3;
  } finally {
    URL.revokeObjectURL(url);
  }
};

export const computeWaveformSamples = async (
  audioBlob: Blob,
  barCount = 40,
): Promise<{ samples: number[]; duration: number }> =>
  decodeAudioForWaveform(audioBlob, barCount);

export const renderOverlayElementToImage = async (
  element: HTMLElement,
): Promise<HTMLImageElement> => {
  const canvas = await html2canvas(element, {
    backgroundColor: null,
    useCORS: true,
    allowTaint: true,
    logging: false,
    scale: 2,
  });

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = canvas.toDataURL("image/png");
  });
};

const drawContainedSource = (
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  sw: number,
  sh: number,
) => {
  const scale = Math.min(STORY_WIDTH / sw, STORY_HEIGHT / sh);
  const dw = sw * scale;
  const dh = sh * scale;
  const dx = (STORY_WIDTH - dw) / 2;
  const dy = (STORY_HEIGHT - dh) / 2;
  ctx.drawImage(source, dx, dy, dw, dh);
};

const drawContainImage = (
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  sw: number,
  sh: number,
  background?: string,
  backgroundImage?: HTMLImageElement | null,
) => {
  if (background) {
    drawStoryBackground(ctx, background, backgroundImage);
  } else {
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, STORY_WIDTH, STORY_HEIGHT);
  }
  drawContainedSource(ctx, source, sw, sh);
};

const resolveOverlayImg = async (
  overlayElement?: HTMLElement,
  overlayImg?: HTMLImageElement,
): Promise<HTMLImageElement> => {
  if (overlayImg) return overlayImg;
  if (!overlayElement) {
    throw new Error("overlayElement or overlayImg is required");
  }
  return renderOverlayElementToImage(overlayElement);
};

const drawCoverImage = (
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  sw: number,
  sh: number,
) => {
  const scale = Math.max(STORY_WIDTH / sw, STORY_HEIGHT / sh);
  const dw = sw * scale;
  const dh = sh * scale;
  const dx = (STORY_WIDTH - dw) / 2;
  const dy = (STORY_HEIGHT - dh) / 2;
  ctx.drawImage(source, dx, dy, dw, dh);
};

const downloadWhatsAppVideo = async (blob: Blob, filename: string, mimeType?: string) => {
  const { ensureWhatsAppCompatibleVideo } = await import("./videoTranscodeUtils");
  const mp4Blob = await ensureWhatsAppCompatibleVideo(blob, mimeType);
  downloadBlob(mp4Blob, `${filename}.mp4`);
};

type VideoOverlayExportParams = {
  videoBlob: Blob;
  overlayElement?: HTMLElement;
  overlayImg?: HTMLImageElement;
  overlayPosition: { x: number; y: number };
  previewWidth: number;
  previewHeight: number;
  mirrorVideo?: boolean;
  background?: string;
  filename: string;
  onProgress?: import("./videoTranscodeUtils").ExportProgressCallback;
  startProgress?: number;
};

const exportVideoWithOverlayRealtime = async ({
  videoBlob,
  overlayElement,
  overlayImg,
  overlayPosition,
  previewWidth,
  previewHeight,
  mirrorVideo,
  background,
  filename,
  onProgress,
  startProgress,
}: VideoOverlayExportParams): Promise<void> => {
  const report = createMonotonicProgress(onProgress);
  const reportRecording = createScopedProgress(report, startProgress ?? 0, 95);
  const mimeType = pickVideoMimeType();
  if (!mimeType) throw new Error("Video export not supported");

  const resolvedOverlayImg = await resolveOverlayImg(overlayElement, overlayImg);
  const backgroundImage = background
    ? await loadStoryBackgroundImage(background)
    : null;
  const scaled = scaleOverlayFromPreview(
    resolvedOverlayImg.width,
    resolvedOverlayImg.height,
    overlayPosition,
    previewWidth,
    previewHeight,
    true,
  );

  const video = document.createElement("video");
  video.muted = false;
  video.playsInline = true;
  const videoUrl = URL.createObjectURL(videoBlob);
  video.src = videoUrl;

  await new Promise<void>((resolve, reject) => {
    video.onloadedmetadata = () => resolve();
    video.onerror = () => reject(new Error("Failed to load video"));
  });

  const canvas = document.createElement("canvas");
  canvas.width = STORY_WIDTH;
  canvas.height = STORY_HEIGHT;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");

  const canvasStream = canvas.captureStream(30);
  const audioCtx = new AudioContext();
  const source = audioCtx.createMediaElementSource(video);
  const audioDest = audioCtx.createMediaStreamDestination();
  source.connect(audioDest);

  const combined = new MediaStream([
    ...canvasStream.getVideoTracks(),
    ...audioDest.stream.getAudioTracks(),
  ]);

  const recorder = new MediaRecorder(combined, { mimeType });
  const chunks: Blob[] = [];

  recorder.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data);
  };

  await new Promise<void>((resolve, reject) => {
    recorder.onstop = () => {
      void (async () => {
        try {
          const blob = new Blob(chunks, { type: mimeType });
          await downloadWhatsAppVideo(blob, filename, mimeType);
          report(100);
          URL.revokeObjectURL(videoUrl);
          void audioCtx.close();
          resolve();
        } catch (err) {
          reject(err);
        }
      })();
    };
    recorder.onerror = () => reject(new Error("Recording failed"));

    const drawVideoFrame = () => {
      if (mirrorVideo) {
        ctx.save();
        ctx.translate(STORY_WIDTH, 0);
        ctx.scale(-1, 1);
        drawContainImage(
          ctx,
          video,
          video.videoWidth,
          video.videoHeight,
          background,
          backgroundImage,
        );
        ctx.restore();
      } else {
        drawContainImage(
          ctx,
          video,
          video.videoWidth,
          video.videoHeight,
          background,
          backgroundImage,
        );
      }
    };

    const draw = () => {
      if (video.ended || video.paused) return;

      const progress =
        Number.isFinite(video.duration) && video.duration > 0
          ? Math.min(1, video.currentTime / video.duration)
          : 0;
      reportRecording(progress);

      drawVideoFrame();
      ctx.drawImage(
        resolvedOverlayImg,
        scaled.x,
        scaled.y,
        scaled.width,
        scaled.height,
      );
      requestAnimationFrame(draw);
    };

    recorder.start(100);
    void video.play();
    requestAnimationFrame(draw);

    video.onended = () => {
      drawVideoFrame();
      ctx.drawImage(
        resolvedOverlayImg,
        scaled.x,
        scaled.y,
        scaled.width,
        scaled.height,
      );
      recorder.stop();
    };
  });
};

export const exportVideoWithOverlay = async (
  params: VideoOverlayExportParams,
): Promise<void> => {
  const report = createMonotonicProgress(params.onProgress);
  try {
    const { exportVideoWithOverlayFast } = await import("./videoOverlayExportUtils");
    await exportVideoWithOverlayFast({ ...params, onProgress: report });
  } catch (error) {
    console.warn("Fast video export failed, falling back to realtime", error);
    await exportVideoWithOverlayRealtime({
      ...params,
      onProgress: report,
      startProgress: report.current,
    });
  }
};

export const parseLinearGradientStops = (
  stopsString: string,
): Array<{ color: string; offset: number }> => {
  const stops = stopsString.split(/,(?![^(]*\))/);
  const parsed: Array<{ color: string; offset: number }> = [];

  stops.forEach((stop, index) => {
    const trimmed = stop.trim();
    const withOffset = trimmed.match(
      /^((?:#[\da-fA-F]{3,8}|rgba?\([^)]+\)))\s+(-?[\d.]+)%$/,
    );

    if (withOffset) {
      parsed.push({
        color: withOffset[1],
        offset: Math.max(0, Math.min(1, parseFloat(withOffset[2]) / 100)),
      });
      return;
    }

    const hexOnly = trimmed.match(/^(#[\da-fA-F]{3,8})$/);
    if (hexOnly) {
      parsed.push({
        color: hexOnly[1],
        offset: index === 0 ? 0 : index === stops.length - 1 ? 1 : index / (stops.length - 1),
      });
    }
  });

  return parsed;
};

const drawGradientBackground = (
  ctx: CanvasRenderingContext2D,
  gradientCss: string,
) => {
  const match = gradientCss.match(
    /linear-gradient\((\d+(?:\.\d+)?)deg,\s*(.+)\)/,
  );
  if (!match) {
    ctx.fillStyle = "#004ef7";
    ctx.fillRect(0, 0, STORY_WIDTH, STORY_HEIGHT);
    return;
  }

  const angleDeg = parseFloat(match[1]);
  const angleRad = ((angleDeg - 90) * Math.PI) / 180;
  const x0 = STORY_WIDTH / 2 - (Math.cos(angleRad) * STORY_WIDTH) / 2;
  const y0 = STORY_HEIGHT / 2 - (Math.sin(angleRad) * STORY_HEIGHT) / 2;
  const x1 = STORY_WIDTH / 2 + (Math.cos(angleRad) * STORY_WIDTH) / 2;
  const y1 = STORY_HEIGHT / 2 + (Math.sin(angleRad) * STORY_HEIGHT) / 2;

  const grad = ctx.createLinearGradient(x0, y0, x1, y1);
  const stops = parseLinearGradientStops(match[2]);

  if (stops.length === 0) {
    ctx.fillStyle = "#004ef7";
    ctx.fillRect(0, 0, STORY_WIDTH, STORY_HEIGHT);
    return;
  }

  stops.forEach(({ color, offset }) => {
    grad.addColorStop(offset, color);
  });

  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, STORY_WIDTH, STORY_HEIGHT);
};

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const extractBackgroundImageUrl = (background: string): string | null => {
  const match = background.match(/url\(([^)]+)\)/);
  if (!match) return null;
  return match[1].replace(/['"]/g, "");
};

const drawStoryBackground = (
  ctx: CanvasRenderingContext2D,
  background: string,
  backgroundImage?: HTMLImageElement | null,
) => {
  if (backgroundImage) {
    drawCoverImage(ctx, backgroundImage, backgroundImage.width, backgroundImage.height);
    return;
  }

  if (background.includes("linear-gradient")) {
    drawGradientBackground(ctx, background);
    return;
  }

  ctx.fillStyle = background.includes("#") ? background : "#004ef7";
  ctx.fillRect(0, 0, STORY_WIDTH, STORY_HEIGHT);
};

const formatVoiceTime = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

export type VoiceBarLayout = {
  x: number;
  y: number;
  width: number;
  height: number;
};

const DEFAULT_VOICE_BAR_LAYOUT: VoiceBarLayout = {
  x: Math.round(STORY_WIDTH * 0.035),
  y: STORY_HEIGHT - Math.round(STORY_HEIGHT * 0.04) - Math.round(STORY_HEIGHT * 0.075),
  width: Math.round(STORY_WIDTH * 0.93),
  height: Math.round(STORY_HEIGHT * 0.075),
};

const clampVoiceBarLayout = (layout: VoiceBarLayout): VoiceBarLayout => {
  const bottomMargin = Math.round(STORY_HEIGHT * 0.04);
  const maxY = STORY_HEIGHT - layout.height - bottomMargin;
  return {
    x: Math.round((STORY_WIDTH - layout.width) / 2),
    y: Math.max(0, Math.min(layout.y, maxY)),
    width: layout.width,
    height: layout.height,
  };
};

export const getStoryScaleFromPreview = (
  previewWidth: number,
  previewHeight: number,
) =>
  Math.min(STORY_WIDTH / previewWidth, STORY_HEIGHT / previewHeight);

const scalePreviewSize = (
  size: { width: number; height: number },
  previewWidth: number,
  previewHeight: number,
) => {
  const scale = getStoryScaleFromPreview(previewWidth, previewHeight);
  return {
    width: size.width * scale,
    height: size.height * scale,
  };
};

export const scaleOverlayFromPreview = (
  canvasWidth: number,
  canvasHeight: number,
  position: { x: number; y: number },
  previewWidth: number,
  previewHeight: number,
  centerX = false,
) => {
  const scale = getStoryScaleFromPreview(previewWidth, previewHeight);
  const width = (canvasWidth / 2) * scale;
  const height = (canvasHeight / 2) * scale;
  const x = centerX ? (STORY_WIDTH - width) / 2 : position.x * scale;
  const y = position.y * scale;
  return { x, y, width, height };
};

const drawPlaybackButtonIcon = (
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
) => {
  const r = size / 2;

  ctx.fillStyle = "#004ef7";
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();

  const barW = size * 0.11;
  const barH = size * 0.36;
  const gap = size * 0.13;
  const radius = barW * 0.35;
  ctx.fillStyle = "#ffffff";
  ctx.beginPath();
  ctx.roundRect(cx - gap / 2 - barW, cy - barH / 2, barW, barH, radius);
  ctx.fill();
  ctx.beginPath();
  ctx.roundRect(cx + gap / 2, cy - barH / 2, barW, barH, radius);
  ctx.fill();
};

const drawVoiceBar = (
  ctx: CanvasRenderingContext2D,
  samples: number[],
  progress: number,
  elapsedSeconds: number,
  layout: VoiceBarLayout = DEFAULT_VOICE_BAR_LAYOUT,
) => {
  const { x: pillX, y: pillY, width: pillW, height: pillH } = layout;
  const s = pillH / 60;
  const cornerRadius = Math.min(28 * s, pillH / 2);
  const padH = 12 * s;
  const iconSize = Math.min(36 * s, pillH * 0.72);
  const iconRadius = iconSize / 2;
  const barAreaH = 36 * s;
  const barAreaY = pillY + (pillH - barAreaH) / 2;

  ctx.fillStyle = "rgba(255,255,255,0.92)";
  ctx.beginPath();
  ctx.roundRect(pillX, pillY, pillW, pillH, cornerRadius);
  ctx.fill();

  const iconCenterX = pillX + padH + iconRadius;
  const iconCenterY = pillY + pillH / 2;

  drawPlaybackButtonIcon(ctx, iconCenterX, iconCenterY, iconSize);

  const timerText = formatVoiceTime(elapsedSeconds);
  const timerFontSize = 14 * s;
  ctx.font = `500 ${timerFontSize}px "Noto Sans Hebrew", sans-serif`;
  ctx.fillStyle = "#122243";
  ctx.textAlign = "right";
  ctx.textBaseline = "middle";
  const timerPad = 14 * s;
  const timerX = pillX + pillW - timerPad;
  ctx.fillText(timerText, timerX, iconCenterY);

  const waveStartX = iconCenterX + iconRadius + 8 * s;
  const waveEndX = timerX - ctx.measureText(timerText).width - 14 * s;
  const waveWidth = Math.max(40 * s, waveEndX - waveStartX);
  const barW = 3 * s;
  const gap = 3 * s;
  const visibleBars = Math.min(
    samples.length,
    Math.floor(waveWidth / (barW + gap)),
  );
  const activeIdx = Math.floor(progress * visibleBars);
  const minBarH = 6 * s;
  const barRadius = 2 * s;

  for (let i = 0; i < visibleBars; i++) {
    const sample = samples[Math.floor((i / visibleBars) * samples.length)] ?? 0.2;
    const h = Math.max(minBarH, sample * barAreaH);
    const x = waveStartX + i * (barW + gap);
    const y = barAreaY + (barAreaH - h) / 2;
    ctx.fillStyle = i <= activeIdx ? "#00eab2" : "rgba(0, 78, 247, 0.35)";
    ctx.beginPath();
    ctx.roundRect(x, y, barW, h, barRadius);
    ctx.fill();
  }
};

export type VoiceStoryLayout = {
  cardX: number;
  cardY: number;
  drawOverlayW: number;
  drawOverlayH: number;
  voiceBarLayout: VoiceBarLayout;
};

export const loadStoryBackgroundImage = async (
  background: string,
): Promise<HTMLImageElement | null> => {
  const imageUrl = extractBackgroundImageUrl(background);
  return imageUrl ? loadImage(imageUrl) : null;
};

export const renderStoryBackgroundToPng = async (
  background: string,
): Promise<Blob> => {
  const canvas = document.createElement("canvas");
  canvas.width = STORY_WIDTH;
  canvas.height = STORY_HEIGHT;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");

  const backgroundImage = await loadStoryBackgroundImage(background);
  drawStoryBackground(ctx, background, backgroundImage);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error("Failed to render background")),
      "image/png",
    );
  });
};

export const resolveVoiceStoryLayout = ({
  overlayImg,
  overlayPosition,
  voiceBarPosition,
  voiceBarSize,
  previewWidth,
  previewHeight,
}: {
  overlayImg: HTMLImageElement;
  overlayPosition?: { x: number; y: number };
  voiceBarPosition?: { x: number; y: number };
  voiceBarSize?: { width: number; height: number };
  previewWidth?: number;
  previewHeight?: number;
}): VoiceStoryLayout => {
  const overlayW = overlayImg.width / 2;
  const overlayH = overlayImg.height / 2;
  const hasCustomLayout =
    overlayPosition &&
    voiceBarPosition &&
    voiceBarSize &&
    previewWidth &&
    previewHeight;

  if (hasCustomLayout) {
    const card = scaleOverlayFromPreview(
      overlayImg.width,
      overlayImg.height,
      overlayPosition,
      previewWidth,
      previewHeight,
      true,
    );
    const barSize = scalePreviewSize(voiceBarSize, previewWidth, previewHeight);
    const scale = getStoryScaleFromPreview(previewWidth, previewHeight);
    const barY = voiceBarPosition.y * scale;

    return {
      cardX: card.x,
      cardY: card.y,
      drawOverlayW: card.width,
      drawOverlayH: card.height,
      voiceBarLayout: clampVoiceBarLayout({
        x: (STORY_WIDTH - barSize.width) / 2,
        y: barY,
        width: barSize.width,
        height: barSize.height,
      }),
    };
  }

  return {
    cardX: (STORY_WIDTH - overlayW) / 2,
    cardY: Math.round(STORY_HEIGHT * 0.206),
    drawOverlayW: overlayW,
    drawOverlayH: overlayH,
    voiceBarLayout: clampVoiceBarLayout(DEFAULT_VOICE_BAR_LAYOUT),
  };
};

export const drawVoiceStoryFrame = (
  ctx: CanvasRenderingContext2D,
  {
    background,
    backgroundImage,
    overlayImg,
    layout,
    samples,
    progress,
    elapsedSeconds,
  }: {
    background: string;
    backgroundImage: HTMLImageElement | null;
    overlayImg: HTMLImageElement;
    layout: VoiceStoryLayout;
    samples: number[];
    progress: number;
    elapsedSeconds: number;
  },
) => {
  drawStoryBackground(ctx, background, backgroundImage);
  ctx.drawImage(
    overlayImg,
    layout.cardX,
    layout.cardY,
    layout.drawOverlayW,
    layout.drawOverlayH,
  );
  drawVoiceBar(
    ctx,
    samples,
    progress,
    elapsedSeconds,
    layout.voiceBarLayout,
  );
};

type VoiceStoryExportParams = {
  audioBlob: Blob;
  overlayElement?: HTMLElement;
  overlayImg?: HTMLImageElement;
  background: string;
  filename: string;
  overlayPosition?: { x: number; y: number };
  voiceBarPosition?: { x: number; y: number };
  voiceBarSize?: { width: number; height: number };
  previewWidth?: number;
  previewHeight?: number;
  onProgress?: import("./videoTranscodeUtils").ExportProgressCallback;
  startProgress?: number;
};

const exportVoiceStoryRealtime = async ({
  audioBlob,
  overlayElement,
  overlayImg,
  background,
  filename,
  overlayPosition,
  voiceBarPosition,
  voiceBarSize,
  previewWidth,
  previewHeight,
  onProgress,
  startProgress,
}: VoiceStoryExportParams): Promise<void> => {
  const report = createMonotonicProgress(onProgress);
  const reportRecording = createScopedProgress(report, startProgress ?? 0, 95);
  const mimeType = pickVideoMimeType();
  if (!mimeType) {
    throw new Error("Video export not supported in this browser");
  }

  const { buffer, audioCtx } = await decodeAudioBlob(audioBlob);
  const { samples, duration } = waveformFromBuffer(buffer);
  const resolvedOverlayImg = await resolveOverlayImg(overlayElement, overlayImg);
  const backgroundImage = await loadStoryBackgroundImage(background);
  const layout = resolveVoiceStoryLayout({
    overlayImg: resolvedOverlayImg,
    overlayPosition,
    voiceBarPosition,
    voiceBarSize,
    previewWidth,
    previewHeight,
  });

  const canvas = document.createElement("canvas");
  canvas.width = STORY_WIDTH;
  canvas.height = STORY_HEIGHT;
  canvas.style.position = "fixed";
  canvas.style.left = "-9999px";
  canvas.style.top = "0";
  document.body.appendChild(canvas);

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    document.body.removeChild(canvas);
    await audioCtx.close();
    throw new Error("Canvas not supported");
  }

  try {
    const bufferSource = audioCtx.createBufferSource();
    bufferSource.buffer = buffer;

    const audioDest = audioCtx.createMediaStreamDestination();
    bufferSource.connect(audioDest);

    const canvasStream = canvas.captureStream(30);
    const combined = new MediaStream([
      ...canvasStream.getVideoTracks(),
      ...audioDest.stream.getAudioTracks(),
    ]);

    const recorder = new MediaRecorder(combined, { mimeType });
    const chunks: Blob[] = [];

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };

    await new Promise<void>((resolve, reject) => {
      const safetyTimeout = window.setTimeout(() => {
        if (recorder.state !== "inactive") recorder.stop();
      }, Math.ceil(duration * 1000) + 3000);

      recorder.onstop = () => {
        window.clearTimeout(safetyTimeout);
        void (async () => {
          try {
            const blob = new Blob(chunks, { type: mimeType });
            if (blob.size === 0) {
              reject(new Error("Empty recording"));
              return;
            }
            report(100);
            await downloadWhatsAppVideo(blob, filename, mimeType);
            resolve();
          } catch (err) {
            reject(err);
          }
        })();
      };

      recorder.onerror = () => {
        window.clearTimeout(safetyTimeout);
        reject(new Error("Recording failed"));
      };

      const startTime = performance.now();

      const draw = () => {
        const elapsed = (performance.now() - startTime) / 1000;
        const progress = Math.min(1, elapsed / Math.max(duration, 0.1));

        drawVoiceStoryFrame(ctx, {
          background,
          backgroundImage,
          overlayImg: resolvedOverlayImg,
          layout,
          samples,
          progress,
          elapsedSeconds: elapsed,
        });

        reportRecording(progress);

        if (recorder.state === "recording") {
          requestAnimationFrame(draw);
        }
      };

      drawVoiceStoryFrame(ctx, {
        background,
        backgroundImage,
        overlayImg: resolvedOverlayImg,
        layout,
        samples,
        progress: 0,
        elapsedSeconds: 0,
      });

      bufferSource.onended = () => {
        drawVoiceStoryFrame(ctx, {
          background,
          backgroundImage,
          overlayImg: resolvedOverlayImg,
          layout,
          samples,
          progress: 1,
          elapsedSeconds: duration,
        });
        window.setTimeout(() => {
          if (recorder.state !== "inactive") recorder.stop();
        }, 200);
      };

      recorder.start(100);
      bufferSource.start(0);
      requestAnimationFrame(draw);
    });
  } catch (error) {
    console.error("Voice story export failed", error);
    throw error;
  } finally {
    document.body.removeChild(canvas);
    await audioCtx.close();
  }
};

export const exportVoiceStory = async (
  params: VoiceStoryExportParams,
): Promise<void> => {
  const report = createMonotonicProgress(params.onProgress);
  try {
    const { exportVoiceStoryFast } = await import("./voiceStoryExportUtils");
    await exportVoiceStoryFast({ ...params, onProgress: report });
  } catch (error) {
    console.warn("Fast voice export failed, falling back to realtime", error);
    await exportVoiceStoryRealtime({
      ...params,
      onProgress: report,
      startProgress: report.current,
    });
  }
};
